import { memo, useCallback, useState } from 'react';
import {
  Button,
  Grid,
  Typography,
  WidgetContainer,
  useSnackbar,
  useTranslations,
  List,
  RenderIf,
} from '@uniqkey-frontend/shared-app';
import { useNavigate } from 'react-router-dom';
import { useGetGroupOrganizations } from '../../../../../../hooks/reactQuery';
import AddOrganizationToGroupWidgetListItem from
  './components/AddOrganizationToGroupWidgetListItem';
import PageRouteEnum from '../../../../../../enums/PageRouteEnum';
import { GroupModuleTabEnum } from '../../../GroupModule';
import AddOrganizationToGroupWidgetNoData from './components/AddOrganizationToGroupWidgetNoData';
import OrganizationsSelectorModal from '../../../OrganizationsSelectorModal';

interface IAddOrganizationToGroupWidgetProps {
  groupId: string;
}

const AddOrganizationToGroupWidget = (props: IAddOrganizationToGroupWidgetProps) => {
  const { groupId } = props;

  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const navigate = useNavigate();

  const {
    data, isLoading, isError, refetch,
  } = useGetGroupOrganizations({
    groupId,
    page: 1,
    pageLength: 2,
  }, {
    onError: () => {
      showError({ text: t('common.somethingWentWrong') });
    },
  });

  const [isOrganizationsSelectorModalOpen, setIsOrganizationsSelectorModalOpen] = useState(false);
  const handleOrganizationsSelectorModalOpen = useCallback(
    () => setIsOrganizationsSelectorModalOpen(true),
    [],
  );
  const handleOrganizationsSelectorModalClose = useCallback(
    () => setIsOrganizationsSelectorModalOpen(false),
    [],
  );

  const handleNavigation = useCallback(() => {
    navigate({
      pathname: `${PageRouteEnum.Groups}/${groupId}/${GroupModuleTabEnum.Organizations}`,
    });
  }, [groupId, navigate]);

  const { data: preOrganizations } = data ?? {};
  const organizations = preOrganizations ?? [];

  if (isError) {
    return null;
  }

  if (!organizations.length && !isLoading) {
    return (
      <AddOrganizationToGroupWidgetNoData
        refetch={refetch}
        groupId={groupId}
      />
    );
  }

  return (
    <WidgetContainer
      container
      flexDirection="column"
      py={3}
      withShadow
      isLoading={isLoading}
    >
      <Grid item>
        <Typography variant="h6">{t('addOrganizationToGroupWidget.title')}</Typography>
      </Grid>
      <List>
        {organizations.map((organization) => (
          <AddOrganizationToGroupWidgetListItem
            groupId={groupId}
            organization={organization}
            key={organization.organizationId}
            refetch={refetch}
          />
        ))}
      </List>
      <Grid container item columnGap={1}>
        <Grid item mt={2}>
          <Button variant="outlined" fullWidth onClick={handleOrganizationsSelectorModalOpen}>
            {t('addOrganizationToGroupWidget.addOrganization')}
          </Button>
        </Grid>
        <RenderIf condition={!!organizations.length}>
          <Grid item mt={2}>
            <Button variant="outlined" fullWidth onClick={handleNavigation}>
              {t('addOrganizationToGroupWidget.viewAll')}
            </Button>
          </Grid>
        </RenderIf>
      </Grid>
      {isOrganizationsSelectorModalOpen && (
        <OrganizationsSelectorModal
          isOpen={isOrganizationsSelectorModalOpen}
          onClose={handleOrganizationsSelectorModalClose}
          groupId={groupId}
          refetch={refetch}
        />
      )}
    </WidgetContainer>
  );
};

export default memo(AddOrganizationToGroupWidget);
